import type { AnchoredRow, UnitMapping } from '../types/anchoredRowTypes'
import { getOrderedUnitUuid, type OrderedWitnessUnit } from './ledgerSafeBoundarySource'
import { buildUnitReferenceIndexWithRemap } from './manualUnitIdentity'

export interface LedgerSafeResolvedMapping {
  originalUnitUuid: string
  unitUuid: string
  unitLabel?: string
  orderedIndex: number | null
  resolution: 'reference' | 'label' | 'text' | 'unresolved'
}

export const getLedgerSafeActiveMapping = (
  row: AnchoredRow,
  witnessId: string
): UnitMapping | undefined =>
  row.unitMappings.find(mapping => mapping.witnessId === witnessId && !mapping.excluded)

export const normalizeLedgerSafeMappedUnitText = (
  value: string | null | undefined
): string =>
  String(value || '')
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim()

export const buildLedgerSafeMappingReferenceIndex = ({
  orderedUnits,
  idRemap
}: {
  orderedUnits: OrderedWitnessUnit[]
  idRemap?: Record<string, string[]> | null
}): Map<string, number> =>
  buildUnitReferenceIndexWithRemap({
    units: orderedUnits.map(entry => entry.unit),
    idRemap
  })

export const resolveLedgerSafeMappingToOrderedIndex = ({
  mapping,
  orderedUnits,
  orderedIndexByReference,
  claimedOrderedIndexes
}: {
  mapping: UnitMapping | null | undefined
  orderedUnits: OrderedWitnessUnit[]
  orderedIndexByReference: Map<string, number>
  claimedOrderedIndexes?: Set<number>
}): LedgerSafeResolvedMapping | null => {
  if (!mapping?.unitUuid) {
    return null
  }

  const originalUnitUuid = mapping.unitUuid
  const references = [mapping.unitUuid, mapping.unitLabel]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map(value => value.trim())

  for (const reference of references) {
    const orderedIndex = orderedIndexByReference.get(reference)
    if (typeof orderedIndex !== 'number') {
      continue
    }
    if (claimedOrderedIndexes?.has(orderedIndex)) {
      return {
        originalUnitUuid,
        unitUuid: originalUnitUuid,
        unitLabel: mapping.unitLabel,
        orderedIndex: null,
        resolution: 'unresolved'
      }
    }
    const unit = orderedUnits[orderedIndex]?.unit
    return {
      originalUnitUuid,
      unitUuid: unit ? getOrderedUnitUuid(unit) : originalUnitUuid,
      unitLabel: mapping.unitLabel,
      orderedIndex,
      resolution: reference === mapping.unitUuid ? 'reference' : 'label'
    }
  }

  const mappedText = normalizeLedgerSafeMappedUnitText(mapping.textSnapshot)
  if (!mappedText) {
    return {
      originalUnitUuid,
      unitUuid: originalUnitUuid,
      unitLabel: mapping.unitLabel,
      orderedIndex: null,
      resolution: 'unresolved'
    }
  }

  const candidates = orderedUnits
    .map((entry, orderedIndex) => ({
      orderedIndex,
      unitUuid: getOrderedUnitUuid(entry.unit),
      text: normalizeLedgerSafeMappedUnitText(entry.unit.unit_text_content)
    }))
    .filter(candidate => candidate.text === mappedText && !claimedOrderedIndexes?.has(candidate.orderedIndex))

  if (candidates.length !== 1) {
    return {
      originalUnitUuid,
      unitUuid: originalUnitUuid,
      unitLabel: mapping.unitLabel,
      orderedIndex: null,
      resolution: 'unresolved'
    }
  }

  return {
    originalUnitUuid,
    unitUuid: candidates[0].unitUuid || originalUnitUuid,
    unitLabel: mapping.unitLabel,
    orderedIndex: candidates[0].orderedIndex,
    resolution: 'text'
  }
}

export const buildLedgerSafeResolvedMappedUnitUuidSet = ({
  rows,
  witnessId,
  orderedUnits,
  idRemap
}: {
  rows: AnchoredRow[]
  witnessId: string
  orderedUnits: OrderedWitnessUnit[]
  idRemap?: Record<string, string[]> | null
}): Set<string> => {
  const orderedIndexByReference = buildLedgerSafeMappingReferenceIndex({
    orderedUnits,
    idRemap
  })
  const claimedOrderedIndexes = new Set<number>()
  const resolvedUnitUuids = new Set<string>()

  rows.forEach(row => {
    const mapping = getLedgerSafeActiveMapping(row, witnessId)
    const resolved = resolveLedgerSafeMappingToOrderedIndex({
      mapping,
      orderedUnits,
      orderedIndexByReference,
      claimedOrderedIndexes
    })
    if (!resolved) {
      return
    }
    if (typeof resolved.orderedIndex === 'number') {
      claimedOrderedIndexes.add(resolved.orderedIndex)
    }
    resolvedUnitUuids.add(resolved.unitUuid)
  })

  return resolvedUnitUuids
}
